import React, { useState, useEffect, useRef } from 'react';
import { Heart } from 'lucide-react';

interface AdBannerProps {
  variant: 'banner' | 'sidebar' | 'inline';
  isPremium?: boolean;
  className?: string;
}

declare global {
  interface Window {
    adsbygoogle?: any[];
  }
}

const ADSENSE_CLIENT = 'ca-pub-2698645556820654';
const ADSENSE_SLOT = '9423601359';

/**
 * Loads the AdSense script dynamically (once) only when an AdBanner is actually
 * rendered inside an editorial content page. This prevents AdSense from
 * activating on app-only / tool screens (Dashboard, Workflow, Upload, etc.),
 * which Google rejects as "screens without publisher content".
 */
let adsenseScriptLoaded = false;
function ensureAdSenseScript(): void {
  if (adsenseScriptLoaded) return;
  if (typeof window === 'undefined') return;

  // Don't load on localhost — Google won't serve ads anyway
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') {
    adsenseScriptLoaded = true;
    return;
  }

  // Check if the script is already in the DOM (e.g. from a previous SPA navigation)
  if (document.querySelector('script[src*="pagead2.googlesyndication.com"]')) {
    adsenseScriptLoaded = true;
    return;
  }

  const script = document.createElement('script');
  script.async = true;
  script.crossOrigin = 'anonymous';
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
  document.head.appendChild(script);
  adsenseScriptLoaded = true;
}

export const AdBanner: React.FC<AdBannerProps> = ({ variant, isPremium = false, className = '' }) => {
  const [adBlocked, setAdBlocked] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const adRef = useRef<HTMLDivElement>(null);
  const adPushedRef = useRef(false);

  const consent = typeof window !== 'undefined' ? localStorage.getItem('vidly_cookie_consent') : null;
  const shouldHide = isPremium || consent !== 'all';

  useEffect(() => {
    if (shouldHide) return;

    const wasDismissed = sessionStorage.getItem('vidly_adblock_dismissed');
    if (wasDismissed) setDismissed(true);

    // Load the AdSense script dynamically only when this component mounts
    // inside an editorial page (GuidesView)
    ensureAdSenseScript();

    // Push ad to AdSense queue if available
    const pushTimer = setTimeout(() => {
      try {
        if (!adPushedRef.current && window.adsbygoogle) {
          window.adsbygoogle.push({});
          adPushedRef.current = true;
        }
      } catch (e) {
        // AdSense push error or adblock
      }
    }, 500);

    // Check if an ad blocker prevented loading after 2.5s
    const blockTimer = setTimeout(() => {
      if (adRef.current) {
        const el = adRef.current.querySelector('[data-ad-slot]') as HTMLElement;
        if (el && (el.offsetHeight === 0 || el.offsetParent === null)) {
          setAdBlocked(true);
        }
      }
    }, 2500);

    return () => {
      clearTimeout(pushTimer);
      clearTimeout(blockTimer);
    };
  }, [shouldHide]);

  if (shouldHide) return null;

  const handleDismissAdblock = () => {
    setDismissed(true);
    sessionStorage.setItem('vidly_adblock_dismissed', 'true');
  };

  // Anti-adblock gentle soft wall
  if (adBlocked && !dismissed) {
    return (
      <div className={`rounded-xl border border-zinc-700 bg-zinc-900/60 p-3 flex items-center justify-between gap-3 text-xs ${className}`}>
        <div className="flex items-center gap-2 text-zinc-300">
          <Heart className="w-3.5 h-3.5 flex-shrink-0 text-zinc-400" />
          <span>Vidly es gratis gracias a la publicidad. Desactiva tu bloqueador para apoyarnos 💛</span>
        </div>
        <button
          onClick={handleDismissAdblock}
          className="text-zinc-400 hover:text-white text-[10px] font-medium flex-shrink-0 px-2 py-1 rounded-lg border border-zinc-700 hover:bg-zinc-800 transition-colors"
        >
          Entendido
        </button>
      </div>
    );
  }

  const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  // Horizontal Banner
  if (variant === 'banner') {
    return (
      <div ref={adRef} className={`w-full flex justify-center ${className}`}>
        <div className="relative w-full max-w-[728px]">
          <span className="absolute -top-4 left-0 text-[9px] text-zinc-600 uppercase tracking-wider">Publicidad</span>
          <div
            data-ad-slot={ADSENSE_SLOT}
            className="w-full min-h-[50px] sm:min-h-[80px] rounded-xl border border-zinc-800/60 bg-zinc-900/30 flex items-center justify-center overflow-hidden p-2"
          >
            {isLocal ? (
              <div className="flex items-center gap-2.5 text-zinc-400 text-xs py-3 px-4 text-center">
                <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0"></span>
                <span className="text-[11px] leading-relaxed">
                  <strong className="text-zinc-200">AdSense listo (ca-pub-2698645556820654):</strong> Google activará los anuncios reales en cuanto la web esté en tu dominio público (Google no envía anuncios a <code>localhost</code>).
                </span>
              </div>
            ) : (
              <ins
                className="adsbygoogle"
                style={{ display: 'block', width: '100%', minHeight: '90px' }}
                data-ad-client={ADSENSE_CLIENT}
                data-ad-slot={ADSENSE_SLOT}
                data-ad-format="auto"
                data-full-width-responsive="true"
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // Sidebar variant
  if (variant === 'sidebar') {
    return (
      <div ref={adRef} className={className}>
        <span className="block text-[9px] text-zinc-600 uppercase tracking-wider mb-1">Publicidad</span>
        <div
          data-ad-slot={ADSENSE_SLOT}
          className="w-[300px] min-h-[250px] rounded-lg border border-zinc-800/60 bg-zinc-900/40 flex items-center justify-center overflow-hidden"
        >
          <ins
            className="adsbygoogle"
            style={{ display: 'inline-block', width: '300px', height: '250px' }}
            data-ad-client={ADSENSE_CLIENT}
            data-ad-slot={ADSENSE_SLOT}
          />
        </div>
      </div>
    );
  }

  // Inline / Native variant
  return (
    <div ref={adRef} className={className}>
      <div
        data-ad-slot={ADSENSE_SLOT}
        className="rounded-xl border border-zinc-800/50 bg-zinc-900/30 p-4 space-y-2 overflow-hidden"
      >
        <div className="flex items-center justify-between">
          <span className="text-[9px] text-zinc-600 uppercase tracking-wider">Publicidad</span>
          <span className="text-[9px] text-zinc-700">Anuncio Google</span>
        </div>
        <ins
          className="adsbygoogle"
          style={{ display: 'block' }}
          data-ad-client={ADSENSE_CLIENT}
          data-ad-slot={ADSENSE_SLOT}
          data-ad-format="horizontal"
          data-full-width-responsive="true"
        />
      </div>
    </div>
  );
};
